<template>
  <div class="modal fade dq-payroll-modal" id="payroll_overtime_entry_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header">
          <div class="dq-modal-titlewrap">
            <div class="dq-hero-icon dq-hero-blue">
              <i class="ti ti-clock-hour-4"></i>
            </div>
            <div>
              <h4 class="modal-title dq-modal-title mb-0">
                {{ isEditMode ? "Edit Overtime Entry" : "Add Overtime Entry" }}
              </h4>
              <div class="dq-modal-sub">
                Attach overtime to an employee and an exact payroll period.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="resetForm"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="onSubmit">
          <div class="modal-body dq-modal-body pb-0">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label dq-label">Employee</label>
                <div class="dropdown w-100 dq-dropdown-wrap">
                  <button
                    class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center dq-dropdown-btn"
                    type="button"
                    data-bs-toggle="dropdown"
                    @click="onEmployeeDropdownOpen"
                  >
                    <span class="text-truncate d-flex align-items-center gap-2">
                      <i class="ti ti-users"></i>
                      <span class="text-truncate">{{ selectedEmployeeLabel }}</span>
                    </span>
                    <i class="ti ti-chevron-down ms-2"></i>
                  </button>

                  <div class="dropdown-menu p-2 w-100 shadow dq-dropdown" @click.stop>
                    <div class="dq-search">
                      <i class="ti ti-search"></i>
                      <input
                        ref="employeeSearchRef"
                        v-model="employeeQuery"
                        type="text"
                        class="form-control dq-search-input"
                        placeholder="Search employees..."
                        autocomplete="off"
                      />
                    </div>

                    <div class="dq-list" style="max-height: 240px; overflow-y: auto">
                      <button
                        v-for="employee in filteredEmployees"
                        :key="employee.id"
                        type="button"
                        class="dropdown-item dq-item d-flex justify-content-between align-items-center"
                        @click="chooseEmployee(employee)"
                      >
                        <span class="text-truncate">
                          {{ employeeLabel(employee) }}
                        </span>

                        <span
                          v-if="Number(form.employee_id) === Number(employee.id)"
                          class="dq-chip"
                        >
                          <i class="ti ti-check me-1"></i> Selected
                        </span>
                      </button>

                      <div
                        v-if="filteredEmployees.length === 0"
                        class="text-center py-2 small text-muted"
                      >
                        No employee found
                      </div>
                    </div>

                    <div class="d-flex justify-content-between align-items-center pt-2 border-top mt-2">
                      <button
                        type="button"
                        class="btn btn-sm btn-light"
                        @click="clearEmployee"
                      >
                        Clear
                      </button>

                      <span class="dq-help mb-0">
                        {{ filteredEmployees.length }} result(s)
                      </span>
                    </div>
                  </div>

                  <input
                    v-model="form.employee_id"
                    type="text"
                    class="dq-hidden-required"
                    required
                    tabindex="-1"
                    aria-hidden="true"
                  />
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label dq-label">Overtime Type</label>
                <select v-model="form.overtime_id" class="form-select dq-input" required>
                  <option value="">Select overtime type</option>
                  <option
                    v-for="overtime in overtimeOptions"
                    :key="overtime.id"
                    :value="overtime.id"
                  >
                    {{ overtime.name }} ({{ overtime.rate_type }} {{ overtime.rate }}x)
                  </option>
                </select>
              </div>

              <div class="col-md-4">
                <label class="form-label dq-label">Frequency</label>
                <select v-model="form.frequency" class="form-select dq-input" required>
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="daily">Daily</option>
                </select>
              </div>

              <div v-if="form.frequency === 'monthly'" class="col-md-8">
                <label class="form-label dq-label">Payroll Month</label>
                <input v-model="form.period" type="month" class="form-control dq-input" required />
              </div>

              <template v-else>
                <div class="col-md-4">
                  <label class="form-label dq-label">Period Start</label>
                  <input
                    v-model="form.period_start"
                    type="date"
                    class="form-control dq-input"
                    required
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label dq-label">Period End</label>
                  <input
                    v-model="form.period_end"
                    type="date"
                    class="form-control dq-input"
                    required
                  />
                </div>
              </template>

              <div class="col-md-4">
                <label class="form-label dq-label">Hours</label>
                <input
                  v-model.number="form.hours"
                  type="number"
                  min="0"
                  step="0.25"
                  class="form-control dq-input"
                  placeholder="0.00"
                />
              </div>

              <div class="col-md-4">
                <label class="form-label dq-label">Custom Multiplier</label>
                <input
                  v-model.number="form.custom_rate_multiplier"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control dq-input"
                  placeholder="Optional"
                />
              </div>

              <div class="col-md-4">
                <label class="form-label dq-label">Flat Amount Override</label>
                <input
                  v-model.number="form.amount"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control dq-input"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div v-if="error" class="alert alert-danger my-3 dq-alert">{{ error }}</div>
          </div>

          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-white border me-2 dq-btn-muted"
              data-bs-dismiss="modal"
              @click="resetForm"
            >
              Cancel
            </button>
            <button type="submit" class="btn btn-primary dq-btn-primary">
              <span
                v-if="entryStore.isLoading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ isEditMode ? "Save Changes" : "Add Overtime Entry" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <div class="modal fade dq-confirm-modal" id="deleteOvertimeEntryModal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-content">
        <div class="modal-body text-center dq-confirm-body">
          <div class="dq-confirm-icon">
            <i class="ti ti-trash-x"></i>
          </div>
          <h4 class="mb-1 dq-confirm-title">Confirm Delete</h4>
          <p class="mb-3 dq-confirm-sub">
            This overtime entry will be permanently removed.
          </p>
          <div class="d-flex justify-content-center gap-2">
            <button class="btn btn-light dq-btn-muted" data-bs-dismiss="modal">
              Cancel
            </button>
            <button class="btn btn-danger dq-btn-danger" @click="confirmDelete">
              <span
                v-if="entryStore.isLoading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineExpose, nextTick, reactive, ref } from "vue";
import Modal from "bootstrap/js/dist/modal";
import { usePayrollOvertimeEntryStore } from "@/stores/payrollOvertimeEntryStore";

const entryStore = usePayrollOvertimeEntryStore();
const emit = defineEmits(["refresh"]);

const isEditMode = ref(false);
const form = reactive({
  id: null,
  employee_id: "",
  overtime_id: "",
  frequency: "monthly",
  period: "",
  period_start: "",
  period_end: "",
  hours: 0,
  custom_rate_multiplier: null,
  amount: null,
});
const error = ref("");
const employeeQuery = ref("");
const employeeSearchRef = ref(null);

const employeeOptions = computed(() =>
  (entryStore.employees || []).slice().sort((a, b) =>
    employeeLabel(a).localeCompare(employeeLabel(b)),
  ),
);
const selectedEmployeeLabel = computed(() => {
  const match = employeeOptions.value.find(
    (employee) => Number(employee.id) === Number(form.employee_id || 0),
  );
  return match ? employeeLabel(match) : "Select employee";
});
const filteredEmployees = computed(() => {
  const q = employeeQuery.value.trim().toLowerCase();
  if (!q) return employeeOptions.value;
  return employeeOptions.value.filter((employee) => {
    const label = employeeLabel(employee).toLowerCase();
    const code = String(employee.employee_id || "").toLowerCase();
    return label.includes(q) || code.includes(q);
  });
});
const overtimeOptions = computed(() =>
  (entryStore.overtimeDefinitions || []).slice().sort((a, b) =>
    (a.name || "").localeCompare(b.name || ""),
  ),
);

function employeeLabel(employee) {
  const parts = [employee.first_name, employee.middle_name, employee.last_name].filter(Boolean);
  const name = parts.join(" ").trim() || employee.employee_id || "Employee";
  return `${name} (${employee.employee_id || "-"})`;
}

function chooseEmployee(employee) {
  form.employee_id = employee.id;
  employeeQuery.value = "";
  closeBootstrapDropdown();
}

function clearEmployee() {
  form.employee_id = "";
  employeeQuery.value = "";
}

function onEmployeeDropdownOpen() {
  nextTick(() => {
    employeeSearchRef.value?.focus();
  });
}

function closeBootstrapDropdown() {
  document.body.click();
}

function resetForm() {
  form.id = null;
  form.employee_id = "";
  form.overtime_id = "";
  form.frequency = "monthly";
  form.period = "";
  form.period_start = "";
  form.period_end = "";
  form.hours = 0;
  form.custom_rate_multiplier = null;
  form.amount = null;
  error.value = "";
  employeeQuery.value = "";
  isEditMode.value = false;
}

function closeModal(id) {
  const modalEl = document.getElementById(id);
  if (modalEl) {
    modalEl.classList.remove("show");
    modalEl.style.display = "none";
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.removeAttribute("aria-modal");
  }
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

function payload() {
  return {
    employee_id: Number(form.employee_id),
    overtime_id: Number(form.overtime_id),
    frequency: form.frequency,
    period: form.frequency === "monthly" ? form.period : undefined,
    period_start: form.frequency === "monthly" ? undefined : form.period_start,
    period_end: form.frequency === "monthly" ? undefined : form.period_end,
    hours: Number(form.hours || 0),
    custom_rate_multiplier:
      form.custom_rate_multiplier === null || form.custom_rate_multiplier === ""
        ? null
        : Number(form.custom_rate_multiplier),
    amount:
      form.amount === null || form.amount === "" ? null : Number(form.amount),
  };
}

async function onSubmit() {
  error.value = "";
  try {
    if (isEditMode.value) {
      await entryStore.updateEntry(form.id, payload());
    } else {
      await entryStore.createEntry(payload());
    }
    emit("refresh");
    closeModal("payroll_overtime_entry_modal");
    resetForm();
  } catch (err) {
    const details = err?.response?.data?.errors;
    if (details && typeof details === "object") {
      error.value = Object.values(details).flat().join(" ");
      return;
    }
    error.value = err?.response?.data?.message || "Failed to save overtime entry.";
  }
}

async function confirmDelete() {
  error.value = "";
  try {
    await entryStore.deleteEntry(form.id);
    emit("refresh");
    closeModal("deleteOvertimeEntryModal");
    resetForm();
  } catch (err) {
    error.value = err?.response?.data?.message || "Failed to delete overtime entry.";
  }
}

function openForEdit(entry) {
  form.id = entry.id;
  form.employee_id = entry.employee_id;
  form.overtime_id = entry.overtime_id;
  form.frequency = inferFrequency(entry);
  form.period = form.frequency === "monthly" ? entry.period || "" : "";
  form.period_start = entry.period_start || "";
  form.period_end = entry.period_end || "";
  form.hours = Number(entry.hours || 0);
  form.custom_rate_multiplier =
    entry.custom_rate_multiplier === null ? null : Number(entry.custom_rate_multiplier);
  form.amount = entry.amount === null ? null : Number(entry.amount);
  isEditMode.value = true;
  error.value = "";
}

function openForDelete(entry) {
  form.id = entry.id;
  const modal = new Modal(document.getElementById("deleteOvertimeEntryModal"), {
    backdrop: "static",
  });
  modal.show();
}

function inferFrequency(entry) {
  const periodCode = String(entry.period_code || "");
  if (periodCode.startsWith("weekly:")) return "weekly";
  if (periodCode.startsWith("fortnightly:")) return "fortnightly";
  if (periodCode.startsWith("daily:")) return "daily";
  return "monthly";
}

defineExpose({
  resetForm,
  openForEdit,
  openForDelete,
});
</script>

<style scoped>
.dq-dropdown-btn {
  border-radius: 14px;
  min-height: 44px;
}

.dq-dropdown {
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 22px 48px rgba(16, 24, 40, 0.14);
}

.dq-search {
  position: relative;
  margin-bottom: 8px;
}

.dq-search i {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
}

.dq-search-input {
  padding-left: 34px;
  border-radius: 12px;
  min-height: 40px;
}

.dq-item {
  border-radius: 12px;
  padding: 10px 10px;
}

.dq-item:hover {
  background: rgba(59, 130, 246, 0.08);
}

.dq-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.16);
  color: rgba(37, 99, 235, 1);
  font-weight: 850;
  font-size: 0.78rem;
  white-space: nowrap;
}

.dq-hidden-required {
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
  pointer-events: none;
}
</style>
